/**
 * Who is signed in, asked once.
 *
 * ONE `me()` FOR THE WHOLE APP. The alternative — every screen that cares about
 * a role fetching the session itself — puts the same request behind four
 * spinners and lets two screens disagree about the answer while both are on
 * screen. The session is a property of the tab, not of a route, so it is held
 * where the tab is.
 *
 * `loading` IS A THIRD STATE AND NOT `user === null`. Those two are "we have not
 * asked yet" and "we asked, and nobody is signed in", and collapsing them is
 * what makes an app flash a login form at somebody who is already signed in on
 * every reload. Every consumer must branch on `loading` first; App.tsx renders a
 * splash for it.
 *
 * WHY `refresh` RE-ASKS THE SERVER INSTEAD OF TAKING THE USER THE LOGIN RETURNED.
 * `POST /auth/login` answers with the user and a `Set-Cookie`, and the body is
 * not evidence that the browser kept the cookie. Third-party-cookie blocking, a
 * `Secure` cookie on a plain-http origin, a clock skew that expires it on
 * arrival: in every one of those the login looks like it worked and the next
 * request is a 401. `me()` is the only thing that proves the session exists,
 * costs one round trip on an action that happens once a day, and turns a
 * baffling class of bug into an immediate "wrong email or password" on the
 * screen that can do something about it.
 *
 * A FAILED `me()` IS AN ERROR, A 401 IS NOT. lib/auth.ts already draws that
 * line: 401 comes back as `null` because "nobody is signed in" is an answer,
 * and anything else throws because a panel that cannot say who you are is
 * broken rather than logged out. `error` exists so the login screen can say
 * which of the two it is instead of silently offering a form that will also
 * fail.
 *
 * A SESSION THAT EXPIRES MID-TAB COMES BACK HERE, NOT TO THE SCREEN THAT NOTICED.
 * Sessions end on their own schedule — idle timeout, absolute timeout, or an admin
 * revoking them — so the request that finds out is whichever one happened to be
 * next, on whatever screen was open. `api.ts` calls this provider's handler
 * instead, which re-asks `me()`, gets null, and lets App fall through to the login
 * form. No new UI, and no screen has to remember to check. See the guards on the
 * registration below for why it cannot become a loop.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { setSessionLostHandler } from '@/api'
import { auth, type PanelUser } from '@/lib/auth'

export interface AuthState {
  /** Null once the session is known to be absent. Also null while `loading`. */
  user: PanelUser | null
  /** True until the first `me()` settles, and again for every `refresh()`. */
  loading: boolean
  /** Set only when `me()` or `logout()` failed for a reason other than 401. */
  error: string | null
  /** Re-ask the server. Called after a login, an invite acceptance, or a role change. */
  refresh: () => void
  signOut: () => void
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  // The epoch is what `refresh` bumps, so a refresh is an effect re-run rather
  // than a second code path: the abort-on-supersede and the StrictMode guard
  // below are then the same three lines for the mount and for every refresh
  // after it. A counter rather than a boolean because two consecutive refreshes
  // must be two distinct dependency values.
  const [epoch, setEpoch] = useState(0)
  const [state, setState] = useState<{
    user: PanelUser | null
    loading: boolean
    error: string | null
  }>({ user: null, loading: true, error: null })

  useEffect(() => {
    const ac = new AbortController()
    auth
      .me(ac.signal)
      .then((user) => {
        if (ac.signal.aborted) return
        setState({ user, loading: false, error: null })
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        // Not signed out — unreachable. The user stays null because we do not
        // know who they are, and the message says so rather than implying their
        // password was wrong.
        setState({
          user: null,
          loading: false,
          error: e instanceof Error ? e.message : String(e),
        })
      })
    return () => ac.abort()
  }, [epoch])

  const refresh = useCallback(() => {
    // Back to loading, so App shows the splash rather than leaving the login
    // form up while the confirming request is in flight. Without it a correct
    // password leaves the form on screen long enough to be typed into twice.
    setState((s) => ({ ...s, loading: true }))
    setEpoch((n) => n + 1)
  }, [])

  /**
   * The two facts the 401 handler needs, in refs rather than in its closure.
   *
   * A handler registered per state change would have to be torn down and rebuilt
   * on every render, and the window between the two is exactly where a 401 would
   * land on nothing.
   */
  const signedIn = useRef(false)
  const challenged = useRef(false)

  useEffect(() => {
    signedIn.current = state.user !== null
    // Signing out — or never having been signed in — re-arms the challenge, so
    // the NEXT session gets its own single re-check.
    if (state.user === null) challenged.current = false
  }, [state.user])

  useEffect(() => {
    setSessionLostHandler(() => {
      // TWO GUARDS, AND BETWEEN THEM THIS CANNOT LOOP.
      //
      // `signedIn` — with nobody signed in the app is already showing the login
      // form, and re-asking a question we have just had answered would be a
      // second identical round trip.
      //
      // `challenged` — one re-check per signed-in stretch. It also coalesces the
      // several 401s a screen with three parallel fetches produces into one
      // refresh. Its real job is the pathological case: if the panel ever
      // answered `auth_required` on /api while /auth/me still returned a user, it
      // would be contradicting itself, and without this flag the refresh would
      // remount the screen, which would re-fetch, which would 401 again, for
      // ever. With it, the second 401 falls through to the screen's own error —
      // which is a server bug showing as a server bug, and not something the
      // client should be quietly absorbing.
      //
      // The cost of the strict version, stated: a transient 401 that turns out
      // to be nothing spends the one challenge, so a genuine expiry later in the
      // same session renders as an error rather than a login form. Bounded and
      // predictable beats clever here.
      if (!signedIn.current || challenged.current) return
      challenged.current = true
      refresh()
    })
    return () => setSessionLostHandler(null)
  }, [refresh])

  const signOut = useCallback(() => {
    // A WRITE, so a timeout signal rather than a controller tied to this
    // component: the request must not be cancelled by the unmount that its own
    // success causes. Same rule as the agent form's Save.
    auth
      .logout(AbortSignal.timeout(10_000))
      .then(() => setState({ user: null, loading: false, error: null }))
      .catch((e: unknown) => {
        // The local state is cleared either way. Continuing to show a signed-in
        // shell to somebody who pressed Sign out is the worse lie of the two.
        // But the cookie is HttpOnly and only the server can end the session, so
        // a failure here means the session is still alive and a reload will sign
        // them straight back in — which is why it is reported and not swallowed.
        setState({
          user: null,
          loading: false,
          error:
            (e instanceof Error ? e.message : String(e))
            + ' — signed out in this tab, but the server did not confirm it. Close the browser to be certain.',
        })
      })
  }, [])

  const value = useMemo<AuthState>(
    () => ({ ...state, refresh, signOut }),
    [state, refresh, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * The session, for any screen inside the provider.
 *
 * It throws rather than returning a null-shaped default, because a screen that
 * renders with `user: null` when the real answer was "you are an admin" is a
 * screen that quietly hides every control it should have shown, and that is a
 * bug with no symptom. Missing the provider is a wiring mistake and should read
 * like one.
 */
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth used outside AuthProvider')
  return ctx
}
